import { loadStripe } from '@stripe/stripe-js';
import {
  createMembership,
  updateMembership,
  getMembership
} from './firestoreService';
import { MEMBERSHIP_TIERS } from '../constants/membershipTiers';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// PayPal configuration
export const PAYPAL_CONFIG = {
  clientId: process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_PAYPAL_CLIENT_ID_LIVE
    : process.env.REACT_APP_PAYPAL_CLIENT_ID,
  currency: 'USD',
  intent: 'capture'
};

// Create Stripe checkout session
export const createCheckoutSession = async (userId, tierKey, discountCode = null) => {
  try {
    const tier = MEMBERSHIP_TIERS[tierKey.toUpperCase()];
    if (!tier) {
      throw new Error('Invalid membership tier');
    }

    // Always use production URL since local emulator isn't running
    const functionUrl = 'https://us-central1-the-apn.cloudfunctions.net/createCheckoutSession';

    const requestBody = {
      userId,
      tierKey,
      discountCode,
      successUrl: `${window.location.origin}/membership/success`,
      cancelUrl: `${window.location.origin}/membership/cancel`
    };

    console.log('Sending Stripe request to:', functionUrl);
    console.log('Request body:', requestBody);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMessage = 'Payment processing failed. Please try again.';

      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // If we can't parse the error response, use the status
        if (response.status === 404) {
          errorMessage = 'Payment service not found. Please contact support.';
        } else if (response.status === 403) {
          errorMessage = 'Payment service access denied. Please contact support.';
        } else {
          errorMessage = `Payment service error (${response.status}). Please try again.`;
        }
      }

      throw new Error(errorMessage);
    }

    const session = await response.json();
    console.log('Received session data:', session);

    if (session.error) {
      throw new Error(session.error);
    }

    if (!session.id) {
      throw new Error('No session ID received from payment service');
    }

    return session;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    // If it's a network error, provide a user-friendly message
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Payment processing is currently being set up. Please try again in a few minutes or contact support.');
    }
    throw error;
  }
};

// Handle successful payment
export const handlePaymentSuccess = async (sessionId) => {
  try {
    const response = await fetch(`/api/checkout-session/${sessionId}`);
    const session = await response.json();

    if (session.payment_status === 'paid') {
      // Create membership record
      const membershipData = {
        userId: session.metadata.userId,
        tier: session.metadata.tierKey,
        status: 'active',
        startDate: new Date(),
        endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
        stripeCustomerId: session.customer,
        stripeSubscriptionId: session.subscription,
        paymentHistory: [{
          sessionId: sessionId,
          amount: session.amount_total / 100,
          currency: session.currency,
          date: new Date(),
          status: 'completed'
        }]
      };

      await createMembership(session.metadata.userId, membershipData);
      return membershipData;
    }
  } catch (error) {
    console.error('Error handling payment success:', error);
    throw error;
  }
};

// Cancel membership
export const cancelMembership = async (userId) => {
  try {
    const membership = await getMembership(userId);
    
    if (membership && membership.stripeSubscriptionId) {
      const response = await fetch('/api/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscriptionId: membership.stripeSubscriptionId
        }),
      });

      if (response.ok) {
        await updateMembership(userId, {
          status: 'cancelled',
          cancelledAt: new Date()
        });
        return true;
      }
    }
  } catch (error) {
    console.error('Error cancelling membership:', error);
    throw error;
  }
};

// Note: validateDiscountCode function is defined later in the discount functions section

// Get membership status
export const getMembershipStatus = async (userId) => {
  try {
    const membership = await getMembership(userId);
    
    if (!membership) {
      return { status: 'none', tier: null, active: false };
    }

    const now = new Date();
    const endDate = membership.endDate?.toDate?.() || new Date(membership.endDate);
    const isActive = membership.status === 'active' && endDate > now;

    return {
      status: membership.status,
      tier: membership.tier,
      active: isActive,
      endDate: endDate,
      daysRemaining: isActive ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : 0
    };
  } catch (error) {
    console.error('Error getting membership status:', error);
    return { status: 'error', tier: null, active: false };
  }
};

// ============ DISCOUNT FUNCTIONS ============

// Apply discount to price
export const applyDiscount = (originalPrice, discountCode, referrerData = null) => {
  if (!discountCode) {
    return { finalPrice: originalPrice, discountAmount: 0 };
  }

  const discounts = {
    'FOUNDING': { percentage: 50, description: 'Founding Member Discount' },
    'VIP': { percentage: 25, description: 'VIP Member Discount' },
    'REFERRAL': { percentage: 10, description: 'Membership Referral Discount' }, // Updated to 10%
    'STUDENT': { percentage: 30, description: 'Student Discount' },
    'EARLY': { percentage: 15, description: 'Early Bird Discount' }
  };

  // Check if it's a referral code (format: USER-ID-TIMESTAMP or similar)
  if (discountCode.includes('-') && referrerData) {
    // This is a referral code - apply 10% discount
    const discountAmount = (originalPrice * 10) / 100;
    const finalPrice = originalPrice - discountAmount;

    return {
      finalPrice: Math.max(finalPrice, 0),
      discountAmount,
      discountPercentage: 10,
      discountDescription: `Referral Discount (${referrerData.name || 'Member'})`,
      referrerInfo: referrerData
    };
  }

  const discount = discounts[discountCode.toUpperCase()];
  if (!discount) {
    return { finalPrice: originalPrice, discountAmount: 0 };
  }

  const discountAmount = (originalPrice * discount.percentage) / 100;
  const finalPrice = originalPrice - discountAmount;

  return {
    finalPrice: Math.max(finalPrice, 0),
    discountAmount,
    discountPercentage: discount.percentage,
    discountDescription: discount.description
  };
};

// Calculate discounted price
export const calculateDiscountedPrice = (originalPrice, discountPercentage) => {
  const discountAmount = (originalPrice * discountPercentage) / 100;
  return originalPrice - discountAmount;
};

// Validate discount code
export const validateDiscountCode = (discountCode) => {
  const validCodes = ['FOUNDING', 'VIP', 'REFERRAL', 'STUDENT', 'EARLY'];
  return validCodes.includes(discountCode.toUpperCase());
};

// ============ PAYPAL PAYMENT FUNCTIONS ============

// Create PayPal order
export const createPayPalOrder = async (userId, tierKey, discountCode = null) => {
  try {
    const tier = MEMBERSHIP_TIERS[tierKey.toUpperCase()];
    if (!tier) {
      throw new Error('Invalid membership tier');
    }

    console.log('Creating PayPal order with data:', {
      userId,
      tierKey,
      discountCode
    });

    // Calculate final price with discount
    let finalPrice = tier.price;
    let discountAmount = 0;

    if (discountCode) {
      const discountResult = applyDiscount(finalPrice, discountCode);
      finalPrice = discountResult.finalPrice;
      discountAmount = discountResult.discountAmount;
    }

    // Call the Firebase function to create a real PayPal order
    const response = await fetch('https://us-central1-the-apn.cloudfunctions.net/createPayPalOrder', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        tierKey,
        discountCode,
        successUrl: `${window.location.origin}/membership/success?paypal=true&tier=${tierKey}`,
        cancelUrl: `${window.location.origin}/membership/cancel`
      }),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to create PayPal order';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // If we can't parse the error response, use the status
        errorMessage = `PayPal service error (${response.status})`;
      }
      throw new Error(errorMessage);
    }

    const order = await response.json();
    console.log('PayPal order created:', order);

    // PayPal buttons expect just the order ID
    return order.id;
  } catch (error) {
    console.error('Error creating PayPal order:', error);
    throw error;
  }
};

// Handle PayPal payment approval
export const handlePayPalApproval = async (orderData, userId, tierKey) => {
  try {
    const tier = MEMBERSHIP_TIERS[tierKey.toUpperCase()];

    // Create membership record
    const membershipData = {
      userId: userId,
      tier: tierKey,
      status: 'active',
      startDate: new Date(),
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
      paypalOrderId: orderData.id,
      paymentMethod: 'paypal',
      paymentHistory: [{
        orderId: orderData.id,
        amount: parseFloat(orderData.purchase_units[0].amount.value),
        currency: orderData.purchase_units[0].amount.currency_code,
        status: 'completed',
        paymentMethod: 'paypal',
        timestamp: new Date()
      }]
    };

    await createMembership(userId, membershipData);

    return {
      success: true,
      membershipData
    };
  } catch (error) {
    console.error('Error handling PayPal approval:', error);
    throw error;
  }
};

// Handle PayPal payment error
export const handlePayPalError = (error) => {
  console.error('PayPal payment error:', error);
  return {
    success: false,
    error: error.message || 'PayPal payment failed'
  };
};

export default {
  PAYPAL_CONFIG,
  createCheckoutSession,
  createPayPalOrder,
  handlePaymentSuccess,
  handlePayPalApproval,
  handlePayPalError,
  applyDiscount,
  validateDiscountCode,
  calculateDiscountedPrice,
  getMembershipStatus
};
